import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Loading from "../../../common/components/loading/loading";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  addMarketPriceDriverCombination,
  deactivateMarketPriceDriverCombination,
  getMarketPriceDriverCombinations,
  updateMarketPriceDriverCombination,
  updateMarketPriceDriverCombinationCore,
} from "../../../common/services/markets/marketPriceDriverCombinationService";
import { getProductWithQualityAttributes } from "../../../common/services/products/productService";
import { getActiveQualityAttributeValueIdsByMarket } from "../../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import MarketPriceDriverCombinationsTable from "./marketPriceDriverCombinationsTable";

const sortCombinations = (items) =>
  [...(items || [])].sort((left, right) => {
    if (!!left.isCore !== !!right.isCore) {
      return left.isCore ? -1 : 1;
    }

    const leftUpdated = left.updatedDate ? new Date(left.updatedDate).getTime() : 0;
    const rightUpdated = right.updatedDate ? new Date(right.updatedDate).getTime() : 0;
    if (leftUpdated !== rightUpdated) {
      return rightUpdated - leftUpdated;
    }

    return (left.combinationId || "").localeCompare(right.combinationId || "");
  });

const MarketPriceDriverCombinations = ({ allowEdit, marketId, productId }) => {
  const { t } = useTranslation(["markets"]);
  const [isLoading, setIsLoading] = useState(true);
  const [combinations, setCombinations] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [valueLookup, setValueLookup] = useState({});
  const [combinationToDelete, setCombinationToDelete] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [product, activeQualityAttributeValueIds, loadedCombinations] =
        await Promise.all([
          getProductWithQualityAttributes(productId),
          getActiveQualityAttributeValueIdsByMarket(marketId),
          getMarketPriceDriverCombinations(marketId),
        ]);

      const priceDriverAttributes = (product?.qualityAttributes || [])
        .filter((attribute) => attribute.isPriceDriver)
        .sort((left, right) =>
          (left.qualityAttributeName || "").localeCompare(
            right.qualityAttributeName || ""
          )
        );

      const allProductValues = product?.qualityAttributeValues || [];
      const activeValueIdSet = new Set(activeQualityAttributeValueIds || []);
      const availableValues = allProductValues.filter((value) =>
        activeValueIdSet.has(value.qualityAttributeValueId)
      );

      const lookup = {};
      allProductValues.forEach((value) => {
        lookup[value.qualityAttributeValueId] = value;
      });

      setValueLookup(lookup);
      setAttributes(
        priceDriverAttributes.map((attribute) => ({
          qualityAttributeId: attribute.qualityAttributeId,
          qualityAttributeName: attribute.qualityAttributeName,
          values: availableValues
            .filter(
              (value) => value.qualityAttributeId === attribute.qualityAttributeId
            )
            .sort((left, right) =>
              (left.value || "").localeCompare(right.value || "")
            ),
        }))
      );
      setCombinations(sortCombinations(loadedCombinations || []));
    } catch (error) {
      console.error("Error loading market price driver combinations:", error);
      toast.error(t("PriceDriverCombinationLoadFailed"));
      setAttributes([]);
      setValueLookup({});
      setCombinations([]);
    } finally {
      setIsLoading(false);
    }
  }, [marketId, productId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = useCallback(
    async (payload) => {
      try {
        const savedCombination = await addMarketPriceDriverCombination(payload);
        setCombinations((currentCombinations) =>
          sortCombinations([savedCombination, ...currentCombinations])
        );
        toast.success(t("PriceDriverCombinationSaved"));
        return savedCombination;
      } catch (error) {
        console.error("Error saving market price driver combination:", error);
        throw error.response?.data || t("PriceDriverCombinationSaveFailed");
      }
    },
    [t]
  );

  const handleUpdate = useCallback(
    async (payload) => {
      try {
        const updatedCombination = await updateMarketPriceDriverCombination(payload);
        setCombinations((currentCombinations) =>
          sortCombinations(
            currentCombinations.map((currentCombination) =>
              currentCombination.combinationId === updatedCombination.combinationId
                ? updatedCombination
                : currentCombination
            )
          )
        );
        toast.success(t("PriceDriverCombinationUpdated"));
        return updatedCombination;
      } catch (error) {
        console.error("Error updating market price driver combination:", error);
        throw error.response?.data || t("PriceDriverCombinationUpdateFailed");
      }
    },
    [t]
  );

  const handleDelete = useCallback((combination) => {
    setCombinationToDelete(combination);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!combinationToDelete) return;

    try {
      await deactivateMarketPriceDriverCombination(combinationToDelete.combinationId);
      setCombinations((currentCombinations) =>
        currentCombinations.filter(
          (combination) =>
            combination.combinationId !== combinationToDelete.combinationId
        )
      );
      setCombinationToDelete(null);
      toast.success(t("PriceDriverCombinationDeleted"));
    } catch (error) {
      console.error("Error deleting market price driver combination:", error);
      toast.error(error.response?.data || t("PriceDriverCombinationDeleteFailed"));
    }
  }, [combinationToDelete, t]);

  const handleToggleCore = useCallback(
    async (combination, isCore) => {
      try {
        const updatedCombination = await updateMarketPriceDriverCombinationCore(
          combination.combinationId,
          isCore
        );
        setCombinations((currentCombinations) =>
          sortCombinations(
            currentCombinations.map((currentCombination) =>
              currentCombination.combinationId === updatedCombination.combinationId
                ? updatedCombination
                : currentCombination
            )
          )
        );
        toast.success(t("PriceDriverCombinationCoreUpdated"));
      } catch (error) {
        console.error("Error updating market price driver combination core flag:", error);
        toast.error(
          error.response?.data || t("PriceDriverCombinationCoreUpdateFailed")
        );
      }
    },
    [t]
  );

  const helpText = useMemo(() => {
    if (attributes.length === 0) {
      return t("PriceDriverCombinationNoAttributesHelpText");
    }

    return t("PriceDriverCombinationHelpText");
  }, [attributes.length, t]);

  const canAddCombination = useMemo(() => {
    if (attributes.length === 0) {
      return true;
    }

    return attributes.every((attribute) => (attribute.values || []).length > 0);
  }, [attributes]);

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-12 d-flex justify-content-end align-items-center">
          <small className="form-text text-muted m-0">{helpText}</small>
        </div>
      </div>

      <MarketPriceDriverCombinationsTable
        allowEdit={allowEdit}
        attributes={attributes}
        canAddCombination={canAddCombination}
        combinations={combinations}
        marketId={marketId}
        onAdd={handleAdd}
        onDelete={handleDelete}
        onToggleCore={handleToggleCore}
        onUpdate={handleUpdate}
        valueLookup={valueLookup}
      />

      <Modal
        show={!!combinationToDelete}
        onHide={() => setCombinationToDelete(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("DeletePriceDriverCombination")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("ConfirmDeletePriceDriverCombination")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCombinationToDelete(null)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MarketPriceDriverCombinations;
